import type { Rect } from '../util/geometry.ts';

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 8;

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

/** (x, y) is the world point at the screen's top-left; screen = (world - xy) * zoom. */
export class Camera {
  x = 0;
  y = 0;
  zoom = 1;
  onChange: () => void = () => {};

  screenToWorld(sx: number, sy: number): [number, number] {
    return [sx / this.zoom + this.x, sy / this.zoom + this.y];
  }

  worldToScreen(wx: number, wy: number): [number, number] {
    return [(wx - this.x) * this.zoom, (wy - this.y) * this.zoom];
  }

  /** Visible world rect for a viewport of the given CSS size. */
  viewport(width: number, height: number): Rect {
    return { x: this.x, y: this.y, w: width / this.zoom, h: height / this.zoom };
  }

  panBy(dsx: number, dsy: number): void {
    if (!dsx && !dsy) return;
    this.x -= dsx / this.zoom;
    this.y -= dsy / this.zoom;
    this.onChange();
  }

  /** Zooms by `factor`, keeping the world point under screen (sx, sy) fixed. */
  zoomAt(sx: number, sy: number, factor: number): void {
    const next = clampZoom(this.zoom * factor);
    if (next === this.zoom) return;
    const [wx, wy] = this.screenToWorld(sx, sy);
    this.zoom = next;
    this.x = wx - sx / next;
    this.y = wy - sy / next;
    this.onChange();
  }

  /** Centres `r` in the viewport, zooming to fit (never beyond 100%). */
  fit(r: Rect, width: number, height: number, padding = 64): void {
    const zx = (width - padding * 2) / Math.max(r.w, 1);
    const zy = (height - padding * 2) / Math.max(r.h, 1);
    this.zoom = clampZoom(Math.min(zx, zy, 1));
    this.x = r.x + r.w / 2 - width / 2 / this.zoom;
    this.y = r.y + r.h / 2 - height / 2 / this.zoom;
    this.onChange();
  }

  set(x: number, y: number, zoom: number): void {
    this.x = x;
    this.y = y;
    this.zoom = clampZoom(zoom);
    this.onChange();
  }
}
